'use client';

import { useEffect, useState, startTransition } from 'react';

import { resolveApiUrl } from '@/lib/api/resolve-url';

/**
 * Module-scoped LRU cache for authenticated image blob URLs.
 *
 * Why: navigation in qr-review/photo-scanning re-renders new viewer instances
 * for each page. Without a shared cache, every prev/next click refetches the
 * blob — even pages the user just saw. The cache:
 *   - dedupes concurrent fetches of the same URL,
 *   - keeps recently-viewed blob URLs alive across unmounts via refcounting,
 *   - evicts least-recently-used entries (revoking their object URLs) when
 *     the cache exceeds MAX_CACHE_SIZE.
 *
 * Insertion order in `Map` doubles as LRU order: `touch()` moves an entry
 * to the back; eviction walks from the front.
 */

const MAX_CACHE_SIZE = 12;

interface CacheEntry {
  blobUrl: string | null;
  promise: Promise<string | null>;
  refCount: number;
}

const cache = new Map<string, CacheEntry>();

function getAuthToken(): string | null {
  return typeof window !== 'undefined'
    ? localStorage.getItem('auth_token')
    : null;
}

async function fetchAsBlobUrl(url: string): Promise<string | null> {
  try {
    const token = getAuthToken();
    // gitlab-advanced-sast-exclude -- resolveApiUrl() pins every request to the configured API origin and throws on anything else.
    const res = await fetch(resolveApiUrl(url), {
      headers: { Authorization: `Bearer ${token ?? ''}` }
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

function touch(url: string): void {
  const entry = cache.get(url);
  if (!entry) return;
  cache.delete(url);
  cache.set(url, entry);
}

function evictIfNeeded(): void {
  if (cache.size <= MAX_CACHE_SIZE) return;
  for (const [url, entry] of cache) {
    if (cache.size <= MAX_CACHE_SIZE) break;
    if (entry.refCount > 0) continue;
    if (entry.blobUrl) URL.revokeObjectURL(entry.blobUrl);
    cache.delete(url);
  }
}

function ensureEntry(url: string): CacheEntry {
  let entry = cache.get(url);
  if (entry) {
    touch(url);
    return entry;
  }
  const promise = fetchAsBlobUrl(url).then((resolved) => {
    const e = cache.get(url);
    if (e) e.blobUrl = resolved;
    return resolved;
  });
  entry = { blobUrl: null, promise, refCount: 0 };
  cache.set(url, entry);
  evictIfNeeded();
  return entry;
}

/**
 * Warm the cache for a URL without subscribing a component to it.
 * No-op if the URL is null or already cached.
 */
export function prefetchAuthenticatedBlob(url: string | null): void {
  if (!url) return;
  ensureEntry(url);
}

/**
 * Resolves a protected image endpoint to a blob URL outside of a component
 * (e.g. for a page that hasn't scrolled into view yet), reusing the same
 * shared LRU cache/dedup as `useAuthenticatedBlobUrl`.
 */
export function resolveAuthenticatedBlobUrl(
  url: string
): Promise<string | null> {
  return ensureEntry(url).promise;
}

/**
 * Fetches a protected image endpoint as a blob, returns the object URL,
 * and keeps the blob alive in a shared LRU cache so re-navigation is instant.
 */
export function useAuthenticatedBlobUrl(url: string | null): string | null {
  const [prevUrl, setPrevUrl] = useState(url);
  const [blobUrl, setBlobUrl] = useState<string | null>(() =>
    url ? (cache.get(url)?.blobUrl ?? null) : null
  );

  // Resync from cache when url changes — paints the cached blob (if any)
  // synchronously so navigation between cached pages has no loading flash.
  if (prevUrl !== url) {
    setPrevUrl(url);
    setBlobUrl(url ? (cache.get(url)?.blobUrl ?? null) : null);
  }

  useEffect(() => {
    if (!url) return;

    const entry = ensureEntry(url);
    entry.refCount += 1;

    let cancelled = false;
    entry.promise.then((resolved) => {
      if (cancelled) return;
      startTransition(() => setBlobUrl(resolved));
    });

    return () => {
      cancelled = true;
      const e = cache.get(url);
      if (!e) return;
      e.refCount = Math.max(0, e.refCount - 1);
      evictIfNeeded();
    };
  }, [url]);

  return blobUrl;
}
